import { matterDetailQueryKey } from "@/lib/data/static/matter-query-key";
import {
  invalidateSearchQueries,
  isCanonicalSearchQuery,
} from "@/lib/data/react-query/search/invalidation";
import {
  createMedicalTreatmentAppointmentConnectionV1,
  createMedicalTreatmentAppointmentV3,
  deleteMedicalTreatmentAppointmentConnectionV1,
  deleteMedicalTreatmentAppointmentV1,
  updateMedicalTreatmentAppointmentV1,
} from "@/lib/data/requests";
import { queryKeys } from "@/lib/data/static";
import { type APIError, type APIResponse, type Matter } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { difference } from "lodash";
import toast from "react-hot-toast";

export const useCreateMedicalTreatmentAppointment = ({
  suppressSuccessToast = false,
  suppressQueryInvalidation = false,
}: {
  suppressSuccessToast?: boolean;
  suppressQueryInvalidation?: boolean;
}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      data: Parameters<typeof createMedicalTreatmentAppointmentV3>[0],
    ) => createMedicalTreatmentAppointmentV3(data),
    onMutate: (data) => {
      const previousData = queryClient.getQueryData<APIResponse<Matter>>(
        matterDetailQueryKey(data.matterId),
      );

      // Optimistic update
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<Matter> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              medicalTreatmentAppointments: [
                ...oldData.data.medicalTreatmentAppointments,
                {
                  ...data,
                  id: "optimistic",
                },
              ],
            },
          };
        },
      );

      return { previousData };
    },
    onError: (_error, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toast.error("Error creating medical treatment appointment");
    },
    onSettled: async () => {
      if (!suppressQueryInvalidation) {
        await invalidateSearchQueries(queryClient);
        await queryClient.invalidateQueries({
          predicate: (query) => !isCanonicalSearchQuery(query),
          queryKey: [queryKeys.matters],
        });
      }
    },
    onSuccess: () => {
      if (!suppressSuccessToast) {
        toast.success("Medical treatment appointment created successfully");
      }
    },
  });
};

export const useUpdateMedicalTreatmentAppointment = ({
  suppressSuccessToast = false,
  suppressQueryInvalidation = false,
}: {
  suppressSuccessToast?: boolean;
  suppressQueryInvalidation?: boolean;
}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      data: Parameters<typeof updateMedicalTreatmentAppointmentV1>[0],
    ) => updateMedicalTreatmentAppointmentV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<Matter> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              medicalTreatmentAppointments: [
                ...oldData.data.medicalTreatmentAppointments.map(
                  (appointment) =>
                    appointment.id === data.id
                      ? { ...appointment, ...data }
                      : appointment,
                ),
              ],
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData(
          matterDetailQueryKey(data.matterId),
        ),
      };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toastAPIErrorMessage(
        error,
        "Error updating medical treatment appointment",
      );
    },
    onSettled: async () => {
      if (!suppressQueryInvalidation) {
        await invalidateSearchQueries(queryClient);
        await queryClient.invalidateQueries({
          predicate: (query) => !isCanonicalSearchQuery(query),
          queryKey: [queryKeys.matters],
        });
      }
    },
    onSuccess: () => {
      if (!suppressSuccessToast) {
        toast.success("Medical treatment appointment updated successfully");
      }
    },
  });
};

export const useDeleteMedicalTreatmentAppointment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      data: Parameters<typeof deleteMedicalTreatmentAppointmentV1>[0],
    ) => deleteMedicalTreatmentAppointmentV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<Matter> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              medicalTreatmentAppointments:
                oldData.data.medicalTreatmentAppointments.filter(
                  (appointment) => appointment.id !== data.id,
                ),
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData(
          matterDetailQueryKey(data.matterId),
        ),
      };
    },
    onError: (_error, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toast.error("Error deleting medical treatment appointment");
    },
    onSettled: async () => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters],
      });
    },
    onSuccess: () => {
      toast.success("Medical treatment appointment deleted successfully");
    },
  });
};

export const useCreateMedicalTreatmentAppointmentConnection = ({
  suppressQueryInvalidation = false,
}: {
  suppressQueryInvalidation?: boolean;
}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      data: Parameters<typeof createMedicalTreatmentAppointmentConnectionV1>[0],
    ) => createMedicalTreatmentAppointmentConnectionV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<Matter> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              medicalTreatmentAppointmentConnections: [
                ...oldData.data.medicalTreatmentAppointmentConnections,
                data,
              ],
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData(
          matterDetailQueryKey(data.matterId),
        ),
      };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toastAPIErrorMessage(
        error,
        "Error creating medical treatment appointment connection",
      );
    },
    onSettled: async () => {
      if (!suppressQueryInvalidation) {
        await invalidateSearchQueries(queryClient);
        await queryClient.invalidateQueries({
          predicate: (query) => !isCanonicalSearchQuery(query),
          queryKey: [queryKeys.matters],
        });
      }
    },
  });
};

export const useUpdateMedicalTreatmentAppointmentConnections = ({
  suppressQueryInvalidation = false,
}: {
  suppressQueryInvalidation?: boolean;
}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      existingItems,
      newItems,
      appointmentId,
      itemType,
      matter,
    }: {
      matter: Matter;
      existingItems: string[];
      newItems: string[];
      appointmentId: string;
      itemType: "bill" | "plan" | "record";
    }) => {
      const itemsToAdd = difference(newItems, existingItems);
      const itemsToRemove = difference(existingItems, newItems);
      const result = await Promise.all([
        ...itemsToAdd.map((item) => {
          const data = {
            matterId: matter.id,
            medicalTreatmentAppointmentId: appointmentId,
          } as {
            matterId: string;
            medicalTreatmentAppointmentId: string;
            medicalTreatmentBillId?: string;
            medicalTreatmentPlanId?: string;
            medicalTreatmentRecordId?: string;
          };
          if (itemType === "bill") {
            data.medicalTreatmentBillId = item;
          } else if (itemType === "plan") {
            data.medicalTreatmentPlanId = item;
          } else {
            data.medicalTreatmentRecordId = item;
          }
          return createMedicalTreatmentAppointmentConnectionV1(data);
        }),

        ...itemsToRemove.map((item) => {
          const data = {
            matterId: matter.id,
          } as {
            matterId: string;
            id: string;
          };
          if (itemType === "bill") {
            data.id =
              matter.medicalTreatmentAppointmentConnections.find(
                (r) =>
                  r.medicalTreatmentBillId === item &&
                  r.medicalTreatmentAppointmentId === appointmentId,
              )?.id ?? "";
          } else if (itemType === "plan") {
            data.id =
              matter.medicalTreatmentAppointmentConnections.find(
                (r) =>
                  r.medicalTreatmentPlanId === item &&
                  r.medicalTreatmentAppointmentId === appointmentId,
              )?.id ?? "";
          } else {
            data.id =
              matter.medicalTreatmentAppointmentConnections.find(
                (r) =>
                  r.medicalTreatmentRecordId === item &&
                  r.medicalTreatmentAppointmentId === appointmentId,
              )?.id ?? "";
          }
          return deleteMedicalTreatmentAppointmentConnectionV1(data);
        }),
      ]);
      return result;
    },
    onSettled: async () => {
      if (!suppressQueryInvalidation) {
        await invalidateSearchQueries(queryClient);
        await queryClient.invalidateQueries({
          predicate: (query) => !isCanonicalSearchQuery(query),
          queryKey: [queryKeys.matters],
        });
      }
    },
  });
};

export const useDeleteMedicalTreatmentAppointmentConnection = ({
  suppressQueryInvalidation = false,
}: {
  suppressQueryInvalidation?: boolean;
}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      data: Parameters<typeof deleteMedicalTreatmentAppointmentConnectionV1>[0],
    ) => deleteMedicalTreatmentAppointmentConnectionV1(data),
    onMutate: (data) => {
      const removedConnection = queryClient
        .getQueryData<APIResponse<Matter>>(matterDetailQueryKey(data.matterId))
        ?.data.medicalTreatmentAppointmentConnections.find(
          (connection) => connection.id === data.id,
        );

      // Optimistic update
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<Matter> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              medicalTreatmentAppointmentConnections:
                oldData.data.medicalTreatmentAppointmentConnections.filter(
                  (connection) => connection.id !== data.id,
                ),
            },
          };
        },
      );

      return { removedConnection };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      const removedConnection = context?.removedConnection;
      if (removedConnection) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          (currentData: APIResponse<Matter> | undefined) => {
            if (
              !currentData ||
              currentData.data.medicalTreatmentAppointmentConnections.some(
                (connection) => connection.id === removedConnection.id,
              )
            ) {
              return currentData;
            }

            return {
              ...currentData,
              data: {
                ...currentData.data,
                medicalTreatmentAppointmentConnections: [
                  ...currentData.data.medicalTreatmentAppointmentConnections,
                  removedConnection,
                ],
              },
            };
          },
        );
      }
      toastAPIErrorMessage(
        error,
        "Error deleting medical treatment appointment connection",
      );
    },
    onSettled: async () => {
      if (!suppressQueryInvalidation) {
        await invalidateSearchQueries(queryClient);
        await queryClient.invalidateQueries({
          predicate: (query) => !isCanonicalSearchQuery(query),
          queryKey: [queryKeys.matters],
        });
      }
    },
  });
};
